import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import TermsOfService from "./TermsOfService";

// QuoteCarousel Component
function QuoteCarousel() {
  const [activeQuote, setActiveQuote] = useState(0);

  const quotes = [
    {
      text: "Know what you own, and know why you own it.",
      author: "Peter Lynch",
      image: require("../assets/peter_lynch_bw_1771237285499.png")
    },
    {
      text: "I'd rather be in Omaha and avoid all the wallstreet noise.",
      author: "Warren Buffett",
      image: require("../assets/warren_buffett_bw_1771237350675.png")
    },
    {
      text: "It ain't what you don't know that gets you into trouble. It's what you know for sure that just ain't so.",
      author: "Mark Twain",
      image: require("../assets/mark_twain_bw_1771237364812.png")
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveQuote((prev) => (prev + 1) % quotes.length);
    }, 8000); // Rotate every 8 seconds

    return () => clearInterval(interval);
  }, [quotes.length]);

  return (
    <div className="relative h-full w-full flex flex-col justify-center p-12 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black opacity-80 z-0"></div>

      <div className="relative z-10 h-96 w-full max-w-4xl mx-auto">
        {quotes.map((quote, index) => (
          <div
            key={index}
            className={`absolute inset-0 flex flex-col md:flex-row items-center md:items-center md:justify-center gap-8 transition-all duration-1000 ease-in-out ${index === activeQuote
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-10 pointer-events-none"
              }`}
          >
            {/* Image Section */}
            <div className="flex-shrink-0">
              <div className="w-48 h-48 md:w-64 md:h-64 relative group">
                <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-transparent transition-colors duration-300"></div>
                <img
                  src={quote.image}
                  alt={quote.author}
                  className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-105"
                  style={{ maskImage: 'radial-gradient(circle, black 50%, transparent 100%)', WebkitMaskImage: 'radial-gradient(circle, black 50%, transparent 100%)' }}
                />
              </div>
            </div>

            {/* Quote Section */}
            <div className="flex flex-col justify-center max-w-lg text-center md:text-left">
              <div className="mb-6 relative">
                <span className="absolute -top-10 -left-6 text-8xl text-gray-700 font-serif opacity-50">“</span>
                <p className="text-gray-200 text-2xl md:text-3xl font-serif italic leading-relaxed tracking-wide">
                  {quote.text}
                </p>
                <span className="absolute -bottom-10 -right-6 text-8xl text-gray-700 font-serif opacity-50 transform rotate-180">“</span>
              </div>
              <p className="text-blue-400 text-lg font-bold tracking-widest uppercase border-t border-gray-700 pt-4 inline-block self-center md:self-start">
                — {quote.author}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Dots indicator */}
      <div className="absolute bottom-12 left-0 right-0 flex justify-center gap-3 z-10">
        {quotes.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveQuote(index)}
            className={`h-2 rounded-full transition-all duration-500 ${index === activeQuote ? "bg-blue-500 w-12" : "bg-gray-600 w-2 hover:bg-gray-500"
              }`}
            aria-label={`Go to quote ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Login() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [agreed, setAgreed] = useState(true);
  const [showTerms, setShowTerms] = useState(false);
  const [redirectHome, setRedirectHome] = useState(false);
  const navigate = useNavigate();

  const handleGoogleSignIn = useCallback(async (response) => {
    try {
      setError("");
      setLoading(true);

      // Send token to backend for verification
      const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
      const res = await fetch(`${API_URL}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: response.credential }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Authentication failed");
      }

      const data = await res.json();

      // Store token and user info
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("userEmail", data.email);
      localStorage.setItem("userName", data.name || "");
      localStorage.setItem("userPicture", data.picture || "");
      localStorage.removeItem("isGuest");

      // If a caller set a one-shot redirect (e.g. the mobile page wanting the
      // user to return to /mobile after auth), honor it and clear the key.
      const requestedRedirect = localStorage.getItem("postLoginRedirect");
      if (requestedRedirect) {
        localStorage.removeItem("postLoginRedirect");
        navigate(requestedRedirect);
      } else if (!data.is_registered) {
        // First-time user - redirect to preferences page
        navigate("/user-preferences");
      } else {
        // Returning user - redirect to stock view
        navigate("/stockview");
      }
    } catch (err) {
      // Don't strand a postLoginRedirect on failure — clear it so the
      // landscape prompt and other gates resume normal behavior.
      localStorage.removeItem("postLoginRedirect");
      setError(err.message || "Failed to sign in with Google");
      console.error("Google sign-in error:", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Wait for Google Sign-In script to load
    const checkGoogle = setInterval(() => {
      if (window.google && window.google.accounts) {
        setGoogleLoaded(true);
        clearInterval(checkGoogle);

        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
        if (!clientId) {
          setError("Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID environment variable.");
          return;
        }

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleSignIn,
        });

        const buttonDiv = document.getElementById("google-signin-button");
        if (buttonDiv) {
          window.google.accounts.id.renderButton(buttonDiv, {
            theme: "outline",
            size: "large",
            width: "100%",
          });
        }
      }
    }, 100);

    return () => clearInterval(checkGoogle);
  }, [handleGoogleSignIn]);

  // Handle id_token returned in URL hash from the direct Google OAuth redirect
  // (used by LandingV2's "Login" / "Sign Up for Free" buttons and the mobile
  // "Sign Up Free" button).
  //
  // React 18's StrictMode mounts components twice in dev. The first run reads
  // and CLEARS the hash; without this guard the second run would see an empty
  // hash and incorrectly redirect to /home — causing a flash of the desktop
  // landing (and its landscape prompt) before the token exchange finishes.
  const hashProcessedRef = useRef(false);
  useEffect(() => {
    if (hashProcessedRef.current) return;

    const hash = window.location.hash;
    if (!hash || !hash.includes("id_token=")) {
      // No OAuth token in hash — someone visited /login directly, send them to /home
      setRedirectHome(true);
      return;
    }

    const params = new URLSearchParams(hash.substring(1)); // strip leading '#'
    const idToken = params.get("id_token");
    if (idToken) {
      hashProcessedRef.current = true;
      // Clear the fragment from the URL immediately so it's not reprocessed
      window.history.replaceState(null, "", window.location.pathname);
      handleGoogleSignIn({ credential: idToken });
    } else {
      setRedirectHome(true);
    }
  }, [handleGoogleSignIn]);

  // Redirect to /home if visited directly (no OAuth token)
  if (redirectHome) {
    return <Navigate to="/home" replace />;
  }

  // While processing the Google token — show a minimal dark loading screen
  return (
    <div className="flex h-screen bg-gray-900 items-center justify-center">
      <TermsOfService isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <div className="text-center">
        {error ? (
          <>
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={() => { window.history.back(); }}
              className="text-blue-400 hover:underline text-sm"
            >
              ← Go back
            </button>
          </>
        ) : (
          <>
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4" />
            <p className="text-gray-300 font-medium">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
